import { useMemo, useState } from "react";
import {
  App,
  Avatar,
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Channel, ChannelProduct, PriceSnapshot } from "@glow-price/shared";
import {
  ENTITY_STATUSES,
  PRODUCT_CATEGORIES,
  formatPriceCNY,
} from "@glow-price/shared";

import {
  brandsApi,
  channelProductsApi,
  channelsApi,
  priceSnapshotsApi,
  standardProductsApi,
  type AdminStandardProductRow,
} from "../api/resources";

const CATEGORY_OPTIONS = Object.values(PRODUCT_CATEGORIES).map((v) => ({ value: v, label: v }));

const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({ value: v, label: v }));

/**
 * 矩阵页：横轴为前 10 个 active 渠道，纵轴为按筛选条件分页的标准商品。
 * 每个 cell 表示某 (standardProduct × channel) 组合是否已经有对应的 channel_product；
 * - 有：显示渠道 logo + channelProductId，点击打开抽屉查看 / 编辑
 * - 无：显示 "+"，点击打开 Modal 创建新的 channel_product
 *
 * 数据来源：标准商品分页 + 全量 active 渠道 + 一次性拉所有 channel_products
 * （admin 数据量可控，5000 上限够用）。最新价格仅在抽屉中按需懒加载，避免
 * N × M 个 cell 触发巨量请求。
 */
export function ChannelProductsMatrixPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();

  const [brandId, setBrandId] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [page, setPage] = useState(1);

  const [cpDrawerOpen, setCpDrawerOpen] = useState(false);
  const [cpDrawerCP, setCpDrawerCP] = useState<ChannelProduct | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createCtx, setCreateCtx] = useState<{
    productId: string;
    productName: string;
    channelId: string;
    channelName: string;
  } | null>(null);
  const [createForm] = Form.useForm<Partial<ChannelProduct>>();

  const brandsQuery = useQuery({
    queryKey: ["brands", "matrix"],
    queryFn: () => brandsApi.list({ page: 1, pageSize: 500 }),
  });
  const brandOptions = (brandsQuery.data?.items ?? []).map((b) => ({
    value: b.id,
    label: `${b.nameCn}${b.nameEn ? ` / ${b.nameEn}` : ""}`,
  }));

  // 渠道：前 10 个 active 渠道作为列头。若未来需要更多列，可放分页或横向滚动。
  const channelsQuery = useQuery({
    queryKey: ["channels", "matrix-active"],
    queryFn: () => channelsApi.list({ page: 1, pageSize: 200 }),
  });
  const channels: Channel[] = useMemo(() => {
    const all = channelsQuery.data?.items ?? [];
    return all.filter((c) => c.status === ENTITY_STATUSES.ACTIVE).slice(0, 10);
  }, [channelsQuery.data]);

  const productsQuery = useQuery({
    queryKey: ["matrix-products", brandId, category, page],
    queryFn: () =>
      standardProductsApi.list({
        brandId,
        category,
        page,
        pageSize: 10,
      }),
  });
  const productsRows: AdminStandardProductRow[] = productsQuery.data?.items ?? [];

  // 一次性拉所有 channel_products（admin 数据量上限 5000）。10 个商品 × 10 个渠道
  // 最多映射出 100 个 cell —— 哈希索引一次性建好。
  const cpQuery = useQuery({
    queryKey: ["channel-products", "matrix-all"],
    queryFn: () => channelProductsApi.list({ page: 1, pageSize: 5000 }),
    staleTime: 30_000,
  });

  const cpByKey = useMemo(() => {
    const m = new Map<string, ChannelProduct>();
    for (const cp of cpQuery.data?.items ?? []) {
      m.set(`${cp.standardProductId}::${cp.channelId}`, cp);
    }
    return m;
  }, [cpQuery.data]);

  // ── drawer: 最新价格懒加载
  const latestPriceQuery = useQuery({
    queryKey: ["price-snapshots", "latest", cpDrawerCP?.id],
    queryFn: () =>
      priceSnapshotsApi.list({
        channelProductId: cpDrawerCP!.id,
        page: 1,
        pageSize: 1,
      }),
    enabled: cpDrawerOpen && !!cpDrawerCP,
  });

  const updateCPMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ChannelProduct> }) =>
      channelProductsApi.update(id, body),
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["channel-products"] });
      setCpDrawerOpen(false);
      setCpDrawerCP(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const createCPMutation = useMutation({
    mutationFn: (body: Partial<ChannelProduct>) => channelProductsApi.create(body),
    onSuccess: () => {
      message.success("已创建渠道商品");
      qc.invalidateQueries({ queryKey: ["channel-products"] });
      setCreateOpen(false);
      setCreateCtx(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const handleCreateSubmit = async () => {
    if (!createCtx) return;
    let values: Partial<ChannelProduct>;
    try {
      values = await createForm.validateFields();
    } catch {
      return;
    }
    createCPMutation.mutate({
      ...values,
      standardProductId: createCtx.productId,
      channelId: createCtx.channelId,
      status: ENTITY_STATUSES.ACTIVE,
    });
  };

  const renderCell = (product: AdminStandardProductRow, channel: Channel) => {
    const cp = cpByKey.get(`${product.product.id}::${channel.id}`);
    if (cp) {
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            setCpDrawerCP(cp);
            setCpDrawerOpen(true);
          }}
          style={{
            cursor: "pointer",
            padding: 6,
            borderRadius: 6,
            border: "1px solid #e8e2dc",
            background: "#fff",
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          <Space size={4}>
            {channel.logoUrl ? (
              <Avatar src={channel.logoUrl} shape="square" size={18} />
            ) : (
              <Avatar shape="square" size={18}>
                {channel.name.slice(0, 1)}
              </Avatar>
            )}
            <Typography.Text style={{ fontSize: 12 }} ellipsis>
              {cp.channelProductId}
            </Typography.Text>
          </Space>
        </div>
      );
    }
    return (
      <Button
        type="dashed"
        size="small"
        block
        onClick={() => {
          setCreateCtx({
            productId: product.product.id,
            productName: product.product.name,
            channelId: channel.id,
            channelName: channel.name,
          });
          createForm.resetFields();
          setCreateOpen(true);
        }}
      >
        +
      </Button>
    );
  };

  const columns: ColumnsType<AdminStandardProductRow> = [
    {
      title: "标准商品",
      key: "product",
      fixed: "left",
      width: 240,
      render: (_: unknown, row: AdminStandardProductRow) => (
        <Space>
          {row.product.imageUrl ? (
            <Avatar src={row.product.imageUrl} shape="square" size={32} />
          ) : (
            <Avatar shape="square" size={32}>
              {row.product.name.slice(0, 1)}
            </Avatar>
          )}
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ maxWidth: 180 }} ellipsis>
              {row.product.name}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {row.brand.nameCn} · {row.product.category}
            </Typography.Text>
          </Space>
        </Space>
      ),
    },
    {
      title: "最低",
      key: "lowest",
      width: 90,
      fixed: "left",
      render: (_: unknown, row: AdminStandardProductRow) =>
        formatPriceCNY(row.lowestFinalPrice),
    },
    ...channels.map<ColumnsType<AdminStandardProductRow>[number]>((channel) => ({
      key: channel.id,
      title: (
        <Tooltip title={channel.code}>
          <Space size={4}>
            {channel.logoUrl ? (
              <Avatar src={channel.logoUrl} shape="square" size={18} />
            ) : (
              <Avatar shape="square" size={18}>
                {channel.name.slice(0, 1)}
              </Avatar>
            )}
            <span style={{ fontSize: 12 }}>{channel.name}</span>
          </Space>
        </Tooltip>
      ),
      width: 130,
      render: (_: unknown, row: AdminStandardProductRow) => renderCell(row, channel),
    })),
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          渠道商品矩阵
        </Typography.Title>
        <Space>
          <Select
            allowClear
            placeholder="按品牌"
            options={brandOptions}
            value={brandId}
            onChange={(v) => {
              setPage(1);
              setBrandId(v);
            }}
            style={{ width: 200 }}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
          />
          <Select
            allowClear
            placeholder="按类别"
            options={CATEGORY_OPTIONS}
            value={category}
            onChange={(v) => {
              setPage(1);
              setCategory(v);
            }}
            style={{ width: 160 }}
          />
        </Space>
      </Space>

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: -8 }}>
        每页 10 行 × 最多 10 个 active 渠道列。点击 "+" 创建新的渠道商品；点击格子打开抽屉编辑。
      </Typography.Paragraph>

      <Table<AdminStandardProductRow>
        rowKey={(r) => r.product.id}
        loading={productsQuery.isLoading || channelsQuery.isLoading || cpQuery.isLoading}
        dataSource={productsRows}
        scroll={{ x: 240 + 90 + channels.length * 130 }}
        pagination={{
          current: page,
          pageSize: 10,
          total: productsQuery.data?.total ?? 0,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        columns={columns}
      />

      <Drawer
        open={cpDrawerOpen}
        title="编辑渠道商品"
        width={520}
        destroyOnClose
        onClose={() => {
          setCpDrawerOpen(false);
          setCpDrawerCP(null);
        }}
      >
        {cpDrawerCP && (
          <CPEditForm
            cp={cpDrawerCP}
            latestSnapshot={latestPriceQuery.data?.items?.[0] ?? null}
            latestLoading={latestPriceQuery.isLoading}
            onSubmit={(body) =>
              updateCPMutation.mutate({ id: cpDrawerCP.id, body })
            }
            saving={updateCPMutation.isPending}
          />
        )}
      </Drawer>

      <Modal
        open={createOpen}
        title={
          createCtx
            ? `新建渠道商品 - ${createCtx.channelName} × ${createCtx.productName}`
            : "新建渠道商品"
        }
        onCancel={() => {
          setCreateOpen(false);
          setCreateCtx(null);
        }}
        onOk={handleCreateSubmit}
        confirmLoading={createCPMutation.isPending}
        destroyOnClose
        width={520}
      >
        <Form<Partial<ChannelProduct>> form={createForm} layout="vertical">
          <Form.Item
            name="channelProductId"
            label="渠道商品 ID"
            rules={[{ required: true, message: "请输入渠道方的商品 ID" }]}
          >
            <Input placeholder="渠道方使用的 SKU 标识" />
          </Form.Item>
          <Form.Item
            name="channelProductName"
            label="渠道商品名"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="spec" label="规格">
            <Input placeholder="100ml / 60g" />
          </Form.Item>
          <Form.Item name="productUrl" label="H5 URL">
            <Input />
          </Form.Item>
          <Form.Item name="miniProgramPath" label="小程序 path">
            <Input placeholder="/pages/product/detail?id=..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function CPEditForm({
  cp,
  latestSnapshot,
  latestLoading,
  onSubmit,
  saving,
}: {
  cp: ChannelProduct;
  latestSnapshot: PriceSnapshot | null;
  latestLoading: boolean;
  onSubmit: (body: Partial<ChannelProduct>) => void;
  saving: boolean;
}) {
  const [form] = Form.useForm<Partial<ChannelProduct>>();
  return (
    <Form<Partial<ChannelProduct>>
      form={form}
      layout="vertical"
      initialValues={cp}
      onFinish={onSubmit}
    >
      <Form.Item label="最新价格">
        {latestLoading ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : latestSnapshot ? (
          <Space>
            <Typography.Text strong>
              {formatPriceCNY(latestSnapshot.finalPrice)}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {latestSnapshot.stockStatus} · {latestSnapshot.capturedAt.slice(0, 16).replace("T", " ")}
            </Typography.Text>
          </Space>
        ) : (
          <Typography.Text type="secondary">暂无快照</Typography.Text>
        )}
      </Form.Item>
      <Form.Item name="channelProductId" label="渠道商品 ID" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item
        name="channelProductName"
        label="渠道商品名"
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="spec" label="规格">
        <Input />
      </Form.Item>
      <Form.Item name="productUrl" label="H5 URL">
        <Input />
      </Form.Item>
      <Form.Item name="miniProgramPath" label="小程序 path">
        <Input />
      </Form.Item>
      <Form.Item name="status" label="状态">
        <Select options={STATUS_OPTIONS} />
      </Form.Item>
      <Button type="primary" htmlType="submit" loading={saving}>
        保存
      </Button>
    </Form>
  );
}
